import { useMemo, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add } from '@factorialco/f0-react/icons/app'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import {
  AtsContractTypesEnum,
  AtsJobPostingScheduleTypesEnum,
  AtsOfferLetterTemplatesCrontractTypesDocument,
  AtsSettingsOfferLetterTemplatesDocument,
  AtsSettingsOfferLetterTemplatesItemFragment,
  AtsWorkplaceTypesEnum,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { CreateOfferLetterTemplateModal } from 'modules/ats/pages/settings/TemplatesPage/sections/OfferLetterTemplates/CreateTemplate'

import { workplaceTypeLabel } from './utils'

interface OfferLetterTemplatesTableFilters {
  contractTypes?: AtsContractTypesEnum[]
  workplaceTypes?: AtsWorkplaceTypesEnum[]
  scheduleTypes?: AtsJobPostingScheduleTypesEnum[]
  tags?: string[]
  active?: boolean[]
}

const DEFAULT_FILTERS: OfferLetterTemplatesTableFilters = {}

const toContractTypes = (value: unknown): AtsContractTypesEnum[] | undefined => {
  if (!Array.isArray(value) || value.length === 0) return undefined
  return value.filter((v): v is AtsContractTypesEnum => typeof v === 'string')
}

const toWorkplaceTypes = (value: unknown): AtsWorkplaceTypesEnum[] | undefined => {
  const validValues: AtsWorkplaceTypesEnum[] = ['onsite', 'hybrid', 'remote']
  if (!Array.isArray(value) || value.length === 0) return undefined
  return value.filter((v): v is AtsWorkplaceTypesEnum => validValues.includes(v))
}

const toScheduleTypes = (value: unknown): AtsJobPostingScheduleTypesEnum[] | undefined => {
  const validValues: AtsJobPostingScheduleTypesEnum[] = ['full_time', 'part_time']
  if (!Array.isArray(value) || value.length === 0) return undefined
  return value.filter((v): v is AtsJobPostingScheduleTypesEnum => validValues.includes(v))
}

const toStringArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value) || value.length === 0) return undefined
  return value.filter((v): v is string => typeof v === 'string')
}

const toBoolean = (value: unknown): boolean | undefined => {
  if (!Array.isArray(value) || value.length === 0) return undefined
  return typeof value[0] === 'boolean' ? value[0] : undefined
}

export const OfferLetterTemplatesTable = () => {
  const client = useApolloClient()

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [savedFilters, setSavedFilters] = useLocalStorage<
    OfferLetterTemplatesTableFilters | undefined
  >(LOCAL_STORAGE_KEYS.atsOfferLetterTemplatesTableFilters)

  const source = useGraphqlDataCollectionSource({
    document: AtsSettingsOfferLetterTemplatesDocument,
    mapData: (data) =>
      data.ats.offerLetterTemplatesConnection.nodes.map((node) => ({
        ...node,
        team_id: node.team?.id ?? null,
        legal_entity_id: node.legalEntity?.id ?? null,
      })),
    mapPagination: (data) => ({
      total: data.ats.offerLetterTemplatesConnection.totalCount ?? 0,
    }),
    currentFilters: savedFilters ?? DEFAULT_FILTERS,
    mapVariables: ({ search, filters, sortings }) => {
      const contractTypes = toContractTypes(filters.contractTypes)
      const workplaceTypes = toWorkplaceTypes(filters.workplaceTypes)
      const scheduleTypes = toScheduleTypes(filters.scheduleTypes)
      const tags = toStringArray(filters.tags)
      const active = toBoolean(filters.active)

      setSavedFilters({
        contractTypes,
        workplaceTypes,
        scheduleTypes,
        tags,
        active: active ? [active] : undefined,
      })

      return {
        search,
        contractTypes,
        workplaceTypes,
        scheduleTypes,
        tags,
        active,
        sort: sortings?.[0],
      }
    },
    perPage: 20,
    search: { enabled: true },
    filters: {
      contractTypes: {
        label: i18n.t('ats.settings.templates.offer_letter_templates.filters.contract_types'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: AtsOfferLetterTemplatesCrontractTypesDocument,
              fetchPolicy: 'cache-first',
            })

            return (
              result.data?.ats.contractTypesConnection.nodes.map((contractType) => ({
                label: contractType.label || '',
                value: contractType.id,
              })) || []
            )
          },
        },
      },
      workplaceTypes: {
        label: i18n.t('ats.settings.templates.offer_letter_templates.filters.workplace_types'),
        type: 'in',
        options: {
          options: [
            {
              label: i18n.t('ats.workplace_types.onsite'),
              value: 'onsite',
            },
            {
              label: i18n.t('ats.workplace_types.hybrid'),
              value: 'hybrid',
            },
            {
              label: i18n.t('ats.workplace_types.remote'),
              value: 'remote',
            },
          ],
        },
      },
      scheduleTypes: {
        label: i18n.t('ats.settings.templates.offer_letter_templates.filters.schedule_types'),
        type: 'in',
        options: {
          options: [
            { label: i18n.t('ats.job_schedule.full_time'), value: 'full_time' },
            { label: i18n.t('ats.job_schedule.part_time'), value: 'part_time' },
          ],
        },
      },
      tags: {
        label: i18n.t('ats.settings.templates.offer_letter_templates.filters.tags'),
        type: 'in',
        options: {
          options: [],
        },
      },
      active: {
        label: i18n.t('ats.settings.templates.offer_letter_templates.filters.active'),
        type: 'in',
        options: {
          options: [
            {
              label: i18n.t('ats.settings.templates.offer_letter_templates.filters.active_active'),
              value: true,
            },
            {
              label: i18n.t(
                'ats.settings.templates.offer_letter_templates.filters.active_inactive'
              ),
              value: false,
            },
          ],
        },
      },
    },
    presets: [
      {
        label: i18n.t('ats.settings.templates.offer_letter_templates.filters.active_active'),
        filter: { active: [true] },
      },
      {
        label: i18n.t('ats.settings.templates.offer_letter_templates.filters.active_inactive'),
        filter: { active: [false] },
      },
    ],
    primaryActions: () => {
      return [
        {
          label: i18n.t('ats.settings.templates.offer_letter_templates.create_template'),
          icon: Add,
          onClick: () => setShowCreateModal(true),
        },
      ]
    },
  })

  const tableColumns = useMemo(
    () => [
      {
        label: i18n.t('ats.settings.templates.offer_letter_templates.columns.name'),
        sorting: 'name',
        render: (template: AtsSettingsOfferLetterTemplatesItemFragment) => ({
          type: 'text' as const,
          value: template.name,
        }),
      },
      {
        label: i18n.t('ats.settings.templates.offer_letter_templates.columns.description'),
        render: (template: AtsSettingsOfferLetterTemplatesItemFragment) => ({
          type: 'text' as const,
          value: template.description || '',
        }),
      },
      {
        label: i18n.t('ats.settings.templates.offer_letter_templates.columns.team'),
        sorting: 'team_id',
        render: (template: AtsSettingsOfferLetterTemplatesItemFragment) => ({
          type: 'text' as const,
          value: template.team?.name || '',
        }),
      },
      {
        label: i18n.t('ats.settings.templates.offer_letter_templates.columns.legal_entity'),
        sorting: 'legal_entity_id',
        render: (template: AtsSettingsOfferLetterTemplatesItemFragment) => ({
          type: 'text' as const,
          value: template.legalEntity?.legalName || '',
        }),
      },
      {
        label: i18n.t('ats.settings.templates.offer_letter_templates.columns.workplace_types'),
        render: ({ workplaceTypes }: AtsSettingsOfferLetterTemplatesItemFragment) => {
          const items = workplaceTypes?.filter((type) => type !== null && type !== undefined) || []

          if (items.length > 0) {
            return {
              type: 'tagList' as const,
              value: {
                type: 'raw' as const,
                tags: items.map((type) => ({
                  text: workplaceTypeLabel(type),
                })),
              },
            }
          }
        },
      },

      {
        label: i18n.t('ats.settings.templates.offer_letter_templates.columns.schedule_types'),
        render: ({ scheduleTypes }: AtsSettingsOfferLetterTemplatesItemFragment) => {
          const items = scheduleTypes?.filter((type) => type !== null && type !== undefined) || []
          if (items.length > 0) {
            return {
              type: 'tagList' as const,
              value: {
                type: 'raw' as const,
                tags: items.map((type) => ({
                  text:
                    type === 'full_time'
                      ? i18n.t('ats.job_schedule.full_time')
                      : i18n.t('ats.job_schedule.part_time'),
                })),
              },
            }
          }
        },
      },
      {
        label: i18n.t('ats.settings.templates.offer_letter_templates.columns.tags'),
        render: ({ tags }: AtsSettingsOfferLetterTemplatesItemFragment) => {
          const items = tags?.filter((tag) => tag !== null && tag !== undefined) || []
          if (items.length > 0) {
            return {
              type: 'tagList' as const,
              value: {
                type: 'raw' as const,
                tags: items.map((tag) => ({
                  text: tag,
                })),
              },
            }
          }
        },
      },
      {
        label: i18n.t('ats.settings.templates.offer_letter_templates.columns.created_at'),
        render: ({ createdAt }: AtsSettingsOfferLetterTemplatesItemFragment) =>
          convertDate(createdAt, 'backend_datetime', 'ui_abbreviated_month_year'),
      },
    ],
    []
  )

  const visualizations = useMemo(
    () => [
      {
        type: 'table' as const,
        options: {
          columns: tableColumns,
        },
      },
    ],
    [tableColumns]
  )

  return (
    <>
      <OneDataCollection source={source} visualizations={visualizations} />
      {showCreateModal && (
        <CreateOfferLetterTemplateModal onClose={() => setShowCreateModal(false)} />
      )}
    </>
  )
}
